import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import AddStudentForm from './components/AddStudentForm';
import StudentDetailModal from './components/StudentDetailModal';
import ViewStudentsPanel from './components/ViewStudentsPanel';
import PromoteAllDialog from './components/PromoteAllDialog';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { getClassrooms, createClassroom, updateClassroom, deleteClassroom, promoteAllStudents } from '../../services/classroomService';
import { getStudents, resetPassword, updateStudent } from '../../services/studentService';

const GRADE_LEVELS = ['Primary 1', 'Primary 2', 'Primary 3', 'Primary 4', 'Primary 5', 'Primary 6'];

const ClassroomManagement = () => {
  const [classrooms, setClassrooms] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedGrade, setSelectedGrade] = useState('All Grades');

  // Add classroom form
  const [showAddClassroom, setShowAddClassroom] = useState(false);
  const [newClassroomData, setNewClassroomData] = useState({ classroomName: '', gradeLevel: '' });

  // Edit classroom
  const [editingClassroom, setEditingClassroom] = useState(null);

  // View students panel
  const [viewClassroom, setViewClassroom] = useState(null);

  // Add student form
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [addStudentClassroomId, setAddStudentClassroomId] = useState(null);

  // Student detail modal
  const [viewStudent, setViewStudent] = useState(null);

  // Promote all dialog
  const [promoteClassroom, setPromoteClassroom] = useState(null);

  useEffect(() => {
    fetchClassrooms();
  }, []);

  useEffect(() => {
    if (classrooms?.length > 0) {
      fetchStudents();
    }
  }, [classrooms]);

  const fetchClassrooms = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getClassrooms();
      setClassrooms(data || []);
    } catch (err) {
      setError(err?.message || 'Failed to load classrooms');
    } finally {
      setLoading(false);
    }
  };

  const fetchStudents = async () => {
    try {
      const data = await getStudents(null);
      setStudents(data || []);
    } catch (err) {
      console.error('Error fetching students:', err);
    }
  };

  const getClassroomStudents = (classroomId) =>
    students?.filter(s => s?.classroomId === classroomId);

  const handleCreateClassroom = async (e) => {
    e?.preventDefault();
    if (!newClassroomData?.classroomName?.trim()) return;
    try {
      setError(null);
      const created = await createClassroom(newClassroomData);
      setClassrooms([created, ...classrooms]);
      setNewClassroomData({ classroomName: '', gradeLevel: '' });
      setShowAddClassroom(false);
    } catch (err) {
      setError(err?.message || 'Failed to create classroom');
    }
  };

  const handleUpdateClassroom = async (classroomId, updatedData) => {
    try {
      setError(null);
      const updated = await updateClassroom(classroomId, updatedData);
      setClassrooms(classrooms?.map(c => c?.id === classroomId ? updated : c));
      setEditingClassroom(null);
    } catch (err) {
      setError(err?.message || 'Failed to update classroom');
    }
  };

  const handleDeleteClassroom = async (classroomId) => {
    if (!window.confirm('Are you sure you want to delete this classroom? This action cannot be undone.')) return;
    try {
      setError(null);
      await deleteClassroom(classroomId);
      setClassrooms(classrooms?.filter(c => c?.id !== classroomId));
      if (viewClassroom?.id === classroomId) setViewClassroom(null);
    } catch (err) {
      setError(err?.message || 'Failed to delete classroom');
    }
  };

  const handlePromoteAll = async (nextGrade) => {
    if (!promoteClassroom) return;
    await promoteAllStudents(promoteClassroom?.id, nextGrade);
    // Update classroom grade in local state
    setClassrooms(classrooms?.map(c =>
      c?.id === promoteClassroom?.id ? { ...c, gradeLevel: nextGrade } : c
    ));
    // Update viewClassroom if open
    if (viewClassroom?.id === promoteClassroom?.id) {
      setViewClassroom(prev => ({ ...prev, gradeLevel: nextGrade }));
    }
    setPromoteClassroom(null);
  };

  const handleViewStudent = (studentId) => {
    const student = students?.find(s => s?.id === studentId);
    if (student) {
      const classroom = classrooms?.find(c => c?.id === student?.classroomId);
      setViewStudent({ 
        ...student, 
        classroomName: classroom?.classroomName || 'Not assigned',
        gradeLevel: student?.gradeLevel || classroom?.gradeLevel || ''
      });
    }
  };

  const handleSaveStudent = async (studentId, updatedData) => {
    const updated = await updateStudent(studentId, updatedData);
    setStudents(students?.map(s => s?.id === studentId ? { ...s, ...updated } : s));
    if (viewStudent?.id === studentId) {
      setViewStudent(prev => ({ ...prev, ...updated }));
    }
  };

  const handleRegenerateCredentials = async (studentId) => {
    const updated = await resetPassword(studentId);
    setStudents(students?.map(s => s?.id === studentId ? updated : s));
    if (viewStudent?.id === studentId) {
      setViewStudent(prev => ({ ...prev, username: updated?.username, plainPassword: updated?.plainPassword }));
    }
    return updated;
  };

  const handleAddStudent = () => {
    fetchStudents();
    setShowAddStudent(false);
    setAddStudentClassroomId(null);
  };

  const openAddStudentForClassroom = (classroomId) => {
    setAddStudentClassroomId(classroomId);
    setShowAddStudent(true);
  };

  const getGradeBadgeColor = (grade) => {
    const colors = {
      'Primary 1': 'bg-blue-100 text-blue-700',
      'Primary 2': 'bg-purple-100 text-purple-700',
      'Primary 3': 'bg-green-100 text-green-700',
      'Primary 4': 'bg-yellow-100 text-yellow-700',
      'Primary 5': 'bg-orange-100 text-orange-700',
      'Primary 6': 'bg-red-100 text-red-700',
    };
    return colors?.[grade] || 'bg-muted text-muted-foreground';
  };

  return (
    <>
      <Helmet>
        <title>Classroom Management - LearningTrails</title>
        <meta name="description" content="Manage student accounts, generate credentials, and organize classrooms for learning trails participation" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />

        <main className="pt-16">
          <div className="max-w-5xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">

            {/* Page Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground">Classroom Management</h1>
                <p className="text-sm text-muted-foreground mt-1">
                  {classrooms?.length} classroom{classrooms?.length !== 1 ? 's' : ''} · {students?.length} student{students?.length !== 1 ? 's' : ''}
                </p>
              </div>
              <Button
                iconName="Plus"
                onClick={() => setShowAddClassroom(!showAddClassroom)}
              >
                {showAddClassroom ? 'Cancel' : 'Add Classroom'}
              </Button>
            </div>

            {/* Grade Filter */}
            <div className="flex flex-wrap gap-2 mb-6">
              {['All Grades', ...GRADE_LEVELS]?.map(grade => (
                <button
                  key={grade}
                  onClick={() => setSelectedGrade(grade)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                    selectedGrade === grade
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-card text-muted-foreground border-border hover:border-primary hover:text-primary'
                  }`}
                >
                  {grade}
                </button>
              ))}
            </div>

            {/* Error */}
            {error && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
                <Icon name="AlertCircle" size={18} className="text-red-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Add Classroom Form */}
            {showAddClassroom && (
              <div className="mb-6 bg-card rounded-xl border border-border shadow-sm p-6">
                <h3 className="text-base font-semibold text-foreground mb-4">Create New Classroom</h3>
                <form onSubmit={handleCreateClassroom} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1.5">Classroom Name *</label>
                      <input
                        type="text"
                        value={newClassroomData?.classroomName}
                        onChange={(e) => setNewClassroomData({ ...newClassroomData, classroomName: e?.target?.value })}
                        className="w-full px-3 py-2 border border-input rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                        placeholder="e.g., 4A, Blue Class"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1.5">Grade Level *</label>
                      <select
                        value={newClassroomData?.gradeLevel}
                        onChange={(e) => setNewClassroomData({ ...newClassroomData, gradeLevel: e?.target?.value })}
                        className="w-full px-3 py-2 border border-input rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                        required
                      >
                        <option value="">Select grade level</option>
                        {GRADE_LEVELS?.map(g => (
                          <option key={g} value={g}>{g}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Button type="submit" iconName="Check">Create Classroom</Button>
                    <Button type="button" variant="outline" onClick={() => {
                      setShowAddClassroom(false);
                      setNewClassroomData({ classroomName: '', gradeLevel: '' });
                    }}>Cancel</Button>
                  </div>
                </form>
              </div>
            )}

            {/* Loading */}
            {loading ? (
              <div className="bg-card rounded-xl border border-border p-12 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
                <p className="text-muted-foreground">Loading classrooms...</p>
              </div>
            ) : classrooms?.length === 0 ? (
              <div className="bg-card rounded-xl border border-border p-12 text-center">
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="School" size={28} className="text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">No Classrooms Yet</h3>
                <p className="text-sm text-muted-foreground mb-4">Create your first classroom to start managing students.</p>
                <Button iconName="Plus" onClick={() => setShowAddClassroom(true)}>Create Classroom</Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {classrooms?.filter(classroom =>
                  selectedGrade === 'All Grades' || classroom?.gradeLevel === selectedGrade
                )?.map(classroom => {
                  const classroomStudents = getClassroomStudents(classroom?.id);
                  const activeCount = classroomStudents?.filter(s => s?.status === 'active')?.length;
                  const isEditing = editingClassroom?.id === classroom?.id;

                  return (
                    <div key={classroom?.id} className="bg-card rounded-xl border border-border shadow-sm p-5 flex flex-col gap-4">
                      {/* Card Header */}
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-base font-semibold text-foreground truncate">{classroom?.classroomName}</h3>
                          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                            {classroom?.gradeLevel && (
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${getGradeBadgeColor(classroom?.gradeLevel)}`}>
                                {classroom?.gradeLevel}
                              </span>
                            )}
                            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                              <Icon name="Users" size={12} />
                              {classroomStudents?.length} student{classroomStudents?.length !== 1 ? 's' : ''}
                            </span>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              activeCount > 0 ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'
                            }`}>
                              {activeCount} active
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Edit Grade Inline */}
                      {isEditing && (
                        <div className="flex flex-col gap-2 bg-muted/50 rounded-lg p-3">
                          <div>
                            <label className="block text-xs font-medium text-muted-foreground mb-1">Classroom Name</label>
                            <input
                              type="text"
                              value={editingClassroom?.classroomName || ''}
                              onChange={(e) => setEditingClassroom({ ...editingClassroom, classroomName: e?.target?.value })}
                              className="w-full px-3 py-1.5 border border-input rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                              placeholder="e.g., 2A, Blue Class"
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <select
                              value={editingClassroom?.gradeLevel || ''}
                              onChange={(e) => setEditingClassroom({ ...editingClassroom, gradeLevel: e?.target?.value })}
                              className="flex-1 px-3 py-1.5 border border-input rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                            >
                              <option value="">Select grade</option>
                              {GRADE_LEVELS?.map(g => (
                                <option key={g} value={g}>{g}</option>
                              ))}
                            </select>
                            <Button
                              size="sm"
                              iconName="Check"
                              onClick={() => handleUpdateClassroom(classroom?.id, { classroomName: editingClassroom?.classroomName, gradeLevel: editingClassroom?.gradeLevel })}
                            >
                              Save
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setEditingClassroom(null)}>Cancel</Button>
                          </div>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex flex-wrap gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          iconName="Users"
                          onClick={() => setViewClassroom(classroom)}
                          className="flex-1"
                        >
                          View Students
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          iconName="ArrowUpCircle"
                          onClick={() => setPromoteClassroom(classroom)}
                          className="flex-1"
                        >
                          Promote All
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          iconName="Edit"
                          onClick={() => setEditingClassroom({ id: classroom?.id, gradeLevel: classroom?.gradeLevel || '', classroomName: classroom?.classroomName || '' })}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          iconName="Trash2"
                          onClick={() => handleDeleteClassroom(classroom?.id)}
                          className="text-red-600 hover:text-red-700 hover:border-red-300"
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </main>

        {/* View Students Panel */}
        {viewClassroom && (
          <ViewStudentsPanel
            classroom={viewClassroom}
            students={getClassroomStudents(viewClassroom?.id)}
            onClose={() => setViewClassroom(null)}
            onViewStudent={(studentId) => {
              handleViewStudent(studentId);
            }}
            onAddStudent={() => openAddStudentForClassroom(viewClassroom?.id)}
          />
        )}

        {/* Add Student Modal */}
        {showAddStudent && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50" onClick={() => setShowAddStudent(false)} />
            <div className="relative z-10 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <AddStudentForm
                classrooms={classrooms}
                defaultClassroomId={addStudentClassroomId}
                onAddStudent={handleAddStudent}
                onCancel={() => { setShowAddStudent(false); setAddStudentClassroomId(null); }}
              />
            </div>
          </div>
        )}

        {/* Student Detail Modal */}
        {viewStudent && (
          <StudentDetailModal
            student={viewStudent}
            classrooms={classrooms}
            onClose={() => setViewStudent(null)}
            onSave={handleSaveStudent}
            onRegenerateCredentials={handleRegenerateCredentials}
          />
        )}

        {/* Promote All Dialog */}
        {promoteClassroom && (
          <PromoteAllDialog
            classroom={promoteClassroom}
            studentCount={getClassroomStudents(promoteClassroom?.id)?.length}
            onClose={() => setPromoteClassroom(null)}
            onConfirm={handlePromoteAll}
          />
        )}
      </div>
    </>
  );
};

export default ClassroomManagement;